import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { poolOrderApi, type PoolOrderDetailResponse, type RosterEntry, type LeasedOwnerEntry, type PoolOrderAnalysis, type PoolOrderAnalysisMeta, type AnalysisPick, type AnalysisRiskFlag } from '../services/poolOrderApi';
import { ScoreBadge } from '../components/ScoreBadge';
import { AddPhoneInline } from '../components/AddPhoneInline';
import { CopyButton, stripPoolOrderAndSuffix } from '../components/CopyButton';
import { useRosterSelection, type RosterSelection } from '../components/useRosterSelection';
import { SelectionBar } from '../components/SelectionBar';
import { PushConfirmModal } from '../components/PushConfirmModal';
import { PushedIndicator } from '../components/PushedIndicator';
import { LeadListIndicator } from '../components/LeadListIndicator';
import { SkipMarker } from '../components/SkipMarker';
import { HasContactIndicator } from '../components/HasContactIndicator';
import { SortHeader, compareStrings, compareBool, type SortDir } from '../components/SortHeader';

type Tab = 'force_pool' | 'matched' | 'suggested' | 'leased_owners' | 'watch_list';

const TAB_VALUES: Tab[] = ['force_pool', 'matched', 'suggested', 'leased_owners', 'watch_list'];
const isValidTab = (t: string | null): t is Tab =>
  t !== null && (TAB_VALUES as string[]).includes(t);

const entityChipClass = (type: string | null | undefined): string => {
  if (!type) return 'bg-text-faint/15 text-text-faint';
  if (['llc', 'corp', 'partnership'].includes(type)) return 'bg-amber/15 text-amber';
  if (type === 'trust') return 'bg-blue/15 text-blue';
  if (type === 'individual') return 'bg-green/15 text-green';
  return 'bg-text-faint/15 text-text-faint';
};

export function PoolOrderDetail() {
  const { id } = useParams<{ id: string }>();
  const nav = useNavigate();
  const [data, setData] = useState<PoolOrderDetailResponse | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [scoring, setScoring] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const selection = useRosterSelection(id ? `pool_order:${id}` : undefined);
  const [pushOpen, setPushOpen] = useState(false);
  // Roster sort. null field = tab's default order (force-pool: oldest
  // activity ASC; matched/suggested: investment_score DESC then name).
  const [sortField, setSortField] = useState<'name' | 'entity_type' | 'phone' | null>(null);
  const [sortDir,   setSortDir]   = useState<SortDir>('asc');

  // Inline doc expansion: party_id → docs (lazy-loaded on first
  // expand). Mirrors AoiDetail's pattern; the underlying endpoint is
  // /api/mi/pool-orders/:id/parties/:partyId/docs which scopes the
  // docs to this pool order's TRS overlap.
  type DocRow = Awaited<ReturnType<typeof poolOrderApi.partyDocs>>['documents'][number];
  const [docsByParty, setDocsByParty] = useState<Map<string, DocRow[] | 'loading' | 'error'>>(new Map());
  const [expandedParties, setExpandedParties] = useState<Set<string>>(new Set());
  async function toggleExpand(partyId: string) {
    setExpandedParties(prev => {
      const next = new Set(prev);
      if (next.has(partyId)) next.delete(partyId);
      else                   next.add(partyId);
      return next;
    });
    // Lazy-load on first expand only. Cached for re-expand.
    if (!expandedParties.has(partyId) && !docsByParty.has(partyId) && id) {
      setDocsByParty(prev => { const m = new Map(prev); m.set(partyId, 'loading'); return m; });
      try {
        const r = await poolOrderApi.partyDocs(id, partyId);
        setDocsByParty(prev => { const m = new Map(prev); m.set(partyId, r.documents); return m; });
      } catch {
        setDocsByParty(prev => { const m = new Map(prev); m.set(partyId, 'error'); return m; });
      }
    }
  }
  // Individuals-only filter, URL-persisted.
  const individualsOnly = searchParams.get('individuals_only') === 'true';
  function toggleIndividualsOnly() {
    const next = new URLSearchParams(searchParams);
    if (individualsOnly) next.delete('individuals_only');
    else                 next.set('individuals_only', 'true');
    setSearchParams(next, { replace: false });
  }

  // Active tab is driven by ?tab=<bucket>. When absent, fall back to a
  // data-dependent default ("watch_list" only if there are scored rows;
  // "matched" otherwise). We deliberately do NOT push the inferred
  // default into the URL — leaving the bare /mi/pool-orders/:id URL
  // alone keeps the back-button story clean on the first arrival.
  const tabParam = searchParams.get('tab');
  // Default tab precedence: force_pool (the operational lens) if any
  // candidates exist; else watch_list when scored; else matched.
  const inferredDefault: Tab = data && data.counts.force_pool > 0
    ? 'force_pool'
    : data && data.counts.watch_list > 0 && data.counts.watch_list_scored > 0
      ? 'watch_list'
      : 'matched';
  const tab: Tab = isValidTab(tabParam) ? tabParam : inferredDefault;

  const setTab = (t: Tab) => {
    // setSearchParams pushes a new history entry by default (v6), which
    // is what we want — back button traverses tab changes too.
    const next = new URLSearchParams(searchParams);
    next.set('tab', t);
    setSearchParams(next);
  };

  async function reload() {
    if (!id) return;
    try {
      const r = await poolOrderApi.get(id);
      setData(r);
    } catch (e: any) {
      setErr(e?.message ?? 'Failed to load');
    }
  }
  useEffect(() => { reload(); }, [id]);

  async function runScoring() {
    if (!id) return;
    setScoring(true);
    try {
      await poolOrderApi.scoreUnmatched(id);
      await reload();
    } catch (e: any) {
      setErr(e?.message ?? 'Scoring failed');
    } finally {
      setScoring(false);
    }
  }

  async function runDelete() {
    if (!id || !data) return;
    const dn = data.pool_order.docket_number ?? id.slice(0, 8);
    if (!confirm(
      `Delete pool order ${dn}?\n\n` +
      `This removes the docket row, all extracted names (${data.counts.all}), ` +
      `Appendix A rows (${data.counts.leased_owners}), TRS tuples, and the ` +
      `stored PDFs. Parties surfaced by this docket remain in the DB but ` +
      `lose their back-pointer. Cannot be undone.`
    )) return;
    setDeleting(true);
    try {
      await poolOrderApi.delete(id);
      nav('/mi/pool-orders');
    } catch (e: any) {
      setErr(e?.message ?? 'Delete failed');
      setDeleting(false);
    }
  }

  async function runRetryParse() {
    if (!id) return;
    if (!confirm('Re-parse will replace the names list and any investment scores from a prior run. Continue?')) return;
    setRetrying(true);
    try {
      await poolOrderApi.retryParse(id);
      await reload();
    } catch (e: any) {
      setErr(e?.message ?? 'Retry parse failed');
    } finally {
      setRetrying(false);
    }
  }

  // Set of party_ids currently leased per Appendix A — used for the
  // force-pool subtraction.
  const leasedPartyIds = useMemo(
    () => new Set(
      (data?.leased_owners ?? [])
        .map(o => o.party_id)
        .filter((p): p is string => !!p)
    ),
    [data]
  );

  const isForcePoolRow = (r: RosterEntry): boolean => {
    if (r.bucket !== 'matched' && r.bucket !== 'suggested') return false;
    const pid = r.party_id ?? r.suggested_party_id;
    if (!pid) return false;
    return !leasedPartyIds.has(pid);
  };

  // For tabs that read from the roster:
  // - force_pool: matched+suggested rows whose party is NOT in leased_owners
  // - matched / suggested / watch_list: the existing bucket slice
  // - leased_owners: handled by a separate panel (no roster filter)
  const filteredRoster = useMemo(() => {
    if (!data || tab === 'leased_owners') return [];

    // Pick the slice for this tab.
    let subset = tab === 'force_pool'
      ? data.roster.filter(isForcePoolRow)
      : data.roster.filter(r => r.bucket === tab);

    // Optional "individuals only" filter — applies on top of the tab
    // slice. Checks matched_entity_type first, falls back to suggested.
    if (individualsOnly) {
      subset = subset.filter(r => {
        const et = r.matched_entity_type ?? r.suggested_entity_type;
        return et === 'individual';
      });
    }

    // User-controlled sort overrides the tab default.
    if (sortField !== null) {
      return subset.slice().sort((a, b) => {
        if (sortField === 'name') {
          const an = a.matched_display_name ?? a.suggested_display_name ?? a.name_as_appears;
          const bn = b.matched_display_name ?? b.suggested_display_name ?? b.name_as_appears;
          return compareStrings(an, bn, sortDir);
        }
        if (sortField === 'entity_type') {
          const ae = a.matched_entity_type ?? a.suggested_entity_type ?? null;
          const be = b.matched_entity_type ?? b.suggested_entity_type ?? null;
          return compareStrings(ae, be, sortDir);
        }
        return compareBool(!!a.has_phone, !!b.has_phone, sortDir);
      });
    }

    // Tab defaults: force-pool sorts by oldest activity ASC (longest-
    // held mineral owners make the best leads). NULLS LAST.
    if (tab === 'force_pool') {
      return subset.slice().sort((a, b) => {
        const at = a.oldest_activity ? Date.parse(a.oldest_activity) : Number.POSITIVE_INFINITY;
        const bt = b.oldest_activity ? Date.parse(b.oldest_activity) : Number.POSITIVE_INFINITY;
        return at - bt;
      });
    }
    return subset;
  }, [data, tab, leasedPartyIds, sortField, sortDir, individualsOnly]);

  if (err) {
    return (
      <div className="px-8 py-7 max-w-[1280px] mx-auto">
        <Link to="/mi/pool-orders" className="text-[12px] text-text-faint font-mono hover:text-text-dim">← all pooling orders</Link>
        <div className="mt-4 text-[13px] text-red bg-red/10 border border-red/40 rounded px-4 py-3">{err}</div>
      </div>
    );
  }
  if (!data) {
    return <div className="px-8 py-7 text-[12px] text-text-faint font-mono">loading…</div>;
  }

  const po = data.pool_order;
  const trsLabel = po.trs.map(t => `T${t.township_num}${t.township_dir} R${t.range_num}${t.range_dir} Sec ${t.section}`).join(' · ');
  const isFailed = po.parse_status !== 'complete';

  return (
    <div className="px-8 py-7 max-w-[1280px] mx-auto">
      <Link to="/mi/pool-orders" className="text-[12px] text-text-faint font-mono hover:text-text-dim">← all pooling orders</Link>

      <header className="mt-2 flex items-end justify-between pb-6 border-b border-border gap-6">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-wider text-text-faint">{po.application_type ? po.application_type.toLowerCase() : 'pool order'}</div>
          <div className="font-display text-[28px] leading-tight">
            <span className="text-text-dim">ECMC</span> <em className="text-accent not-italic">{po.docket_number ?? 'unknown'}</em>
            {po.cause_number && <span className="text-text-dim text-[16px] ml-3">cause {po.cause_number}</span>}
          </div>
          <div className="text-[12px] text-text-dim mt-2 font-mono space-x-2">
            <span>{po.operator ?? 'unknown operator'}</span>
            <span className="text-text-ghost">·</span>
            <span>{trsLabel || 'no TRS'}</span>
            <span className="text-text-ghost">·</span>
            <span>{po.filed_date ? `filed ${po.filed_date}` : 'filed —'}</span>
            <span className="text-text-ghost">·</span>
            <span>{po.hearing_date ? `hearing ${po.hearing_date}` : 'hearing —'}</span>
            <span className="text-text-ghost">·</span>
            <span>{data.counts.all} names</span>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {po.pdf_application_r2_key && (
            <a href={poolOrderApi.pdfUrl(po.id, 'application')} target="_blank" rel="noreferrer"
              className="px-3 py-2 text-[12px] text-text-dim border border-border rounded hover:text-text hover:bg-bg-hover">
              View application ↗
            </a>
          )}
          {po.pdf_names_r2_key && (
            <a href={poolOrderApi.pdfUrl(po.id, 'names')} target="_blank" rel="noreferrer"
              className="px-3 py-2 text-[12px] text-text-dim border border-border rounded hover:text-text hover:bg-bg-hover">
              View names list ↗
            </a>
          )}
          <button onClick={runRetryParse} disabled={retrying || deleting}
            className="px-3 py-2 text-[12px] text-text-dim border border-border rounded hover:text-text hover:bg-bg-hover disabled:opacity-50">
            {retrying ? 'Re-parsing…' : '↻ Retry parse'}
          </button>
          <button onClick={runDelete} disabled={deleting || retrying}
            title="Delete this pool order and its children"
            className="px-3 py-2 text-[12px] text-text-dim border border-border rounded hover:text-red hover:border-red/50 disabled:opacity-50">
            {deleting ? 'Deleting…' : 'Delete'}
          </button>
          <button disabled title="session 7"
            className="px-3 py-2 text-[13px] bg-accent/30 text-text-dim font-medium rounded cursor-not-allowed">
            Build call list →
          </button>
        </div>
      </header>

      {isFailed && (
        <div className="mt-4 text-[12px] text-red bg-red/10 border border-red/40 rounded px-4 py-3">
          <div className="font-medium">Parse failed.</div>
          <div className="font-mono text-[11px] mt-1">{(po.parse_warnings ?? []).join(' · ')}</div>
          <div className="mt-2 text-text-dim">Use ↻ Retry parse above to re-run Vision against the stored PDFs, or upload a corrected file.</div>
        </div>
      )}

      {!isFailed && po.parse_warnings && po.parse_warnings.length > 0 && (
        <div className="mt-4 text-[11px] text-amber bg-amber/10 border border-amber/30 rounded px-4 py-2 font-mono">
          {po.parse_warnings.map((w, i) => <div key={i}>{w}</div>)}
        </div>
      )}

      {!isFailed && <AutoRecoveryBanners data={data} onReload={reload} />}

      {po.wellbore_apis.length > 0 && (
        <div className="mt-4 text-[11px] text-text-dim font-mono flex flex-wrap gap-2">
          <span className="uppercase tracking-wider text-text-faint">wells:</span>
          {po.wellbore_apis.map((w, i) => (
            <span key={i} className="px-2 py-0.5 bg-bg-input/60 border border-border rounded">
              {w.name} <span className="text-text-faint">· {w.api}</span>
            </span>
          ))}
        </div>
      )}

      <div className="mt-4 bg-bg-input/40 border border-border rounded px-4 py-2 text-[11px] text-text-dim">
        Colorado orders are name-only.{' '}
        <span className="text-text">Matched</span> owners pull address + activity from your database.{' '}
        <span className="text-text">Suggested</span> need confirmation.{' '}
        <span className="text-text">Watch list</span> needs skip-trace at build time.
      </div>

      <div className="mt-6 grid grid-cols-[1fr_360px] gap-6">
        <div>
          <div className="flex items-center gap-1 border-b border-border">
            {(['force_pool', 'matched', 'suggested', 'leased_owners', 'watch_list'] as Tab[]).map(t => (
              <button key={t} onClick={() => setTab(t)}
                className={`px-3 py-2.5 text-[12px] font-mono uppercase tracking-wide border-b-2 ${
                  tab === t
                    ? 'text-accent-bright border-accent'
                    : 'text-text-dim border-transparent hover:text-text'
                }`}>
                {labelForTab(t)} <span className="text-text-faint ml-1">{(data.counts as any)[t]}</span>
              </button>
            ))}
            <button onClick={toggleIndividualsOnly}
              title="Show only entity_type = individual (hides LLCs, trusts, corps, etc.). Applies to matched/suggested/force-pool tabs."
              className={
                'ml-auto px-3 py-1.5 mb-[-1px] text-[11px] font-mono rounded-t border-t border-l border-r ' +
                (individualsOnly
                  ? 'border-green/40 bg-green/10 text-green'
                  : 'border-border text-text-faint hover:text-text')
              }>
              {individualsOnly ? '✓ Individuals only' : 'Individuals only'}
            </button>
          </div>

          {tab === 'watch_list' && data.counts.watch_list_unscored > 0 && (
            <div className="mt-3 flex items-center justify-between bg-accent-soft border border-accent/30 rounded px-4 py-3">
              <div className="text-[12px] text-text">
                <strong>{data.counts.watch_list_unscored}</strong> watch-list {data.counts.watch_list_unscored === 1 ? 'name has' : 'names have'} not been scored.
                <span className="text-text-dim ml-1">Run skip-trace-ROI scoring to rank them.</span>
              </div>
              <button onClick={runScoring} disabled={scoring}
                className="px-3 py-2 text-[12px] bg-accent text-bg font-medium rounded hover:bg-accent-bright disabled:opacity-50 whitespace-nowrap">
                {scoring ? 'Scoring (~15s)…' : `Run scoring on ${data.counts.watch_list_unscored}`}
              </button>
            </div>
          )}

          {tab === 'watch_list' && data.counts.watch_list > 0 && (
            <CopyAllWatchListButton roster={data.roster} />
          )}

          {tab === 'force_pool' && (
            <div className="mt-3 bg-accent-soft border border-accent/30 rounded px-4 py-3 text-[12px] text-text">
              <strong>Force-pool candidates.</strong>
              <span className="text-text-dim ml-1">
                Parties on this docket whose lessor name does <em>not</em> appear in Appendix A — known to our DB but no current lease in the unit per the operator's filing. Sorted by oldest grantor activity (longest-held first).
              </span>
            </div>
          )}

          {tab === 'leased_owners' ? (
            <LeasedOwnersPanel
              rows={data.leased_owners}
              counts={data.counts}
              operatorAvgTermMonths={data.operator_avg_primary_term_months}
            />
          ) : (
            <div className="mt-3">
              {(tab === 'force_pool' || tab === 'matched' || tab === 'suggested') && filteredRoster.length > 0 && (
                <div className="flex items-center gap-3 px-4 py-2 mb-1 text-[10px] uppercase tracking-wider font-mono text-text-faint border-b border-border">
                  <span>sort:</span>
                  <SortHeader<'name' | 'entity_type' | 'phone'>
                    label="Name" field="name"
                    active={sortField} dir={sortDir}
                    onChange={(f, d) => { setSortField(f); setSortDir(d); }}
                  />
                  <SortHeader<'name' | 'entity_type' | 'phone'>
                    label="Entity" field="entity_type"
                    active={sortField} dir={sortDir}
                    onChange={(f, d) => { setSortField(f); setSortDir(d); }}
                  />
                  <SortHeader<'name' | 'entity_type' | 'phone'>
                    label="Phone" field="phone"
                    active={sortField} dir={sortDir}
                    onChange={(f, d) => { setSortField(f); setSortDir(d); }}
                  />
                  {sortField !== null && (
                    <button onClick={() => setSortField(null)}
                      className="ml-auto text-[10px] text-text-faint hover:text-text font-mono normal-case">
                      reset
                    </button>
                  )}
                </div>
              )}
              {/* "Select N non-sold" bulk action — mirrors the AOI roster. */}
              {/* Counts the matched rows (those with a party_id, the only   */}
              {/* selectable bucket) whose has_sold_minerals is false. The   */}
              {/* button is additive — operator's manual ticks survive.       */}
              {(tab === 'force_pool' || tab === 'matched' || tab === 'suggested') && filteredRoster.length > 0 && (() => {
                const selectable = filteredRoster.filter(r => !!r.party_id);
                const nonSold    = selectable.filter(r => !r.has_sold_minerals);
                const soldCount  = selectable.length - nonSold.length;
                const allSelected = nonSold.length > 0 && nonSold.every(r => selection.isSelected(r.party_id!));
                return (
                  <div className="flex items-center gap-2 text-[11px] font-mono px-4 py-2">
                    <button
                      onClick={() => selection.selectMany(nonSold.map(r => ({
                        party_id: r.party_id!,
                        display_name: r.matched_display_name ?? r.name_as_appears,
                        has_phone: !!r.has_phone,
                        has_address: !!r.has_address,
                        source_label: r.matched_display_name ?? r.name_as_appears,
                      })))}
                      disabled={nonSold.length === 0 || allSelected}
                      title={
                        nonSold.length === 0
                          ? 'No selectable non-sold parties in this view'
                          : allSelected
                            ? 'All non-sold parties in this view are already selected'
                            : `Add ${nonSold.length} non-sold ${nonSold.length === 1 ? 'party' : 'parties'} to selection`
                      }
                      className="px-2.5 py-1 border border-accent/40 text-accent rounded hover:bg-accent-soft disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      + Select {nonSold.length} non-sold
                    </button>
                    {soldCount > 0 && (
                      <span className="text-text-faint">
                        excluding {soldCount} marked <span className="text-text-dim">sold</span>
                      </span>
                    )}
                  </div>
                );
              })()}
              {filteredRoster.length === 0 && (
                <div className="px-4 py-8 text-center text-[12px] text-text-faint font-mono">
                  {tab === 'force_pool'
                    ? 'no force-pool candidates — every known party on this docket is already on Appendix A'
                    : 'no names in this bucket'}
                </div>
              )}
              {filteredRoster.map(r => {
                const expandKey = r.party_id ?? r.suggested_party_id ?? null;
                const isExpanded = expandKey ? expandedParties.has(expandKey) : false;
                const docsState  = expandKey ? docsByParty.get(expandKey) : undefined;
                return (
                  <RosterRow key={r.pool_order_name_id} row={r} onMutated={reload} selection={selection}
                    isExpanded={isExpanded}
                    onToggleExpand={expandKey ? () => toggleExpand(expandKey) : undefined}
                    docsState={docsState} />
                );
              })}
            </div>
          )}
        </div>

        <aside className="space-y-3">
          {id && (
            <PoolOrderMapImageCard
              poolOrderId={id}
              hasImage={data.pool_order.has_map_image}
              uploadedAt={data.pool_order.map_image_uploaded_at}
              onChange={reload}
            />
          )}
          {id && <ClaudeAnalysisCard poolOrderId={id} cached={data.claude_analysis} onAnalyzed={reload} />}
          {po.referenced_order_numbers.length > 0 && (
            <div className="bg-bg-card border border-border rounded p-4">
              <div className="text-[10px] uppercase tracking-wider font-mono text-text-faint mb-2">References</div>
              <div className="space-y-1">
                {po.referenced_order_numbers.map(n => (
                  <div key={n} className="text-[12px] font-mono text-text-dim">{n}</div>
                ))}
              </div>
            </div>
          )}
          {po.formations.length > 0 && (
            <div className="bg-bg-card border border-border rounded p-4">
              <div className="text-[10px] uppercase tracking-wider font-mono text-text-faint mb-2">Formations</div>
              <div className="text-[12px] text-text-dim">{po.formations.join(' · ')}</div>
            </div>
          )}
        </aside>
      </div>

      <SelectionBar
        selection={selection}
        sourceLabel={`Docket ${po.docket_number ?? po.id.slice(0, 8)}`}
        source_kind="pool_order"
        source_id={po.id}
        onPush={() => setPushOpen(true)}
      />

      {pushOpen && id && (
        <PushConfirmModal
          source_kind="pool_order"
          source_id={id}
          selection={selection}
          onClose={() => setPushOpen(false)}
          onPushed={() => { selection.clear(); }}
        />
      )}
    </div>
  );
}

function labelForTab(t: Tab): string {
  if (t === 'watch_list') return 'Watch list';
  if (t === 'leased_owners') return 'Leased owners';
  if (t === 'force_pool') return 'Force-pool';
  return t.charAt(0).toUpperCase() + t.slice(1);
}

// "Copy all watch-list names ↗" — always visible above the watch-list
// table; uses the same TruePeopleSearch formatter the SelectionBar uses,
// scoped to every watch_list row regardless of checkbox state.
function CopyAllWatchListButton({ roster }: { roster: RosterEntry[] }) {
  const [copied, setCopied] = useState(false);
  const watch = roster.filter(r => r.bucket === 'watch_list');
  if (watch.length === 0) return null;

  async function copy() {
    const text = watch
      .map(r => r.name_as_appears.trim().replace(/\s+AND\s*$/i, '').trim())
      .filter(Boolean)
      .join('\n');
    try { await navigator.clipboard.writeText(text); }
    catch {
      const ta = document.createElement('textarea');
      ta.value = text; document.body.appendChild(ta); ta.select();
      document.execCommand('copy'); document.body.removeChild(ta);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  }

  return (
    <div className="mt-3 flex justify-end">
      <button onClick={copy}
        className="px-3 py-1.5 text-[11px] text-text border border-border rounded hover:bg-bg-hover font-mono">
        {copied ? `Copied ${watch.length} ✓` : `Copy all ${watch.length} watch-list names ↗`}
      </button>
    </div>
  );
}

// Per-row docs disclosure state is owned by the parent so docs cache
// + expand set survives sort/filter changes and reloads. RosterRow
// renders the chevron when onToggleExpand is provided and the docs
// table inside its own fragment when isExpanded is true.
type RosterRowDoc = {
  doc_number: string;
  ordinal: number;
  doc_type: string | null;
  recording_date: string | null;
  trs_label: string | null;
};
interface RosterRowProps {
  row: RosterEntry;
  onMutated: () => void | Promise<void>;
  selection: RosterSelection;
  isExpanded?: boolean;
  onToggleExpand?: () => void;
  docsState?: RosterRowDoc[] | 'loading' | 'error' | undefined;
}

function RosterRow({ row, onMutated, selection, isExpanded = false, onToggleExpand, docsState }: RosterRowProps) {
  const [busy, setBusy] = useState<'confirm' | 'reject' | 'promote' | null>(null);
  const [rowErr, setRowErr] = useState<string | null>(null);

  async function withBusy(kind: 'confirm' | 'reject' | 'promote', fn: () => Promise<unknown>) {
    setRowErr(null);
    setBusy(kind);
    try {
      await fn();
      await onMutated();
    } catch (e: any) {
      setRowErr(e?.message ?? 'failed');
    } finally {
      setBusy(null);
    }
  }

  if (row.bucket === 'matched') {
    const checked = !!row.party_id && selection.isSelected(row.party_id);
    return (
      <div className="border-b border-border last:border-b-0">
      <div className="grid grid-cols-[26px_2fr_1.5fr_1.2fr_60px_auto] items-center gap-3 px-4 py-3 hover:bg-bg-hover/30">
        <div>
          {row.party_id && (
            <input type="checkbox" checked={checked} onChange={() => selection.toggle({
              party_id: row.party_id!,
              display_name: row.matched_display_name ?? row.name_as_appears,
              has_phone: !!row.has_phone,
              has_address: !!row.has_address,
              source_label: row.matched_display_name ?? row.name_as_appears,
            })} className="accent-accent" />
          )}
        </div>
        <div>
          <div className="text-[13px] text-text">
            {onToggleExpand && (
              <button
                onClick={onToggleExpand}
                title={isExpanded ? 'Hide documents' : 'Show this party\'s documents in this docket\'s TRS'}
                className="inline-flex items-center justify-center w-4 text-text-faint hover:text-accent text-[11px] mr-0.5 align-middle"
              >{isExpanded ? '▾' : '▸'}</button>
            )}
            {row.name_as_appears}
            <PushedIndicator pushedAt={row.last_pushed_to_close_at} className="ml-1.5" />
            <LeadListIndicator status={row.lead_list_status}         className="ml-1.5" />
            <SkipMarker      skippedAt={row.skipped_at}              className="ml-1.5" />
            {row.has_sold_minerals && (
              <span
                title="Conveyed minerals or royalty within this docket's TRS (grantor on a mineral/royalty deed)"
                className="ml-1.5 px-1.5 py-0.5 text-[9px] font-mono uppercase tracking-wider rounded bg-text-faint/15 text-text-faint align-middle"
              >sold</span>
            )}
          </div>
          <div className="text-[10px] text-text-faint font-mono mt-0.5">
            → {row.party_id ? (
              <Link to={`/mi/party/${row.party_id}`} className="text-accent hover:underline">{row.matched_display_name}</Link>
            ) : <span className="text-accent">{row.matched_display_name}</span>}
          </div>
          <PartyContextChips row={row} />
        </div>
        <div className="flex items-center gap-2">
          <span className={'px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider rounded ' + entityChipClass(row.matched_entity_type)}>
            {row.matched_entity_type ?? '—'}
          </span>
          <span className="text-[10px] font-mono text-text-faint">
            {row.match_confidence?.toFixed(2)}
          </span>
        </div>
        <div className="text-[11px] text-text-dim font-mono">
          {row.matched_last_recording_date ?? <span className="text-text-ghost italic">no activity</span>}
        </div>
        <div className="flex justify-end text-[12px]">
          <HasContactIndicator hasPhone={!!row.has_phone} hasAddress={!!row.has_address} />
        </div>
        <div className="flex justify-end items-center gap-1.5">
          <CopyButton text={row.matched_display_name ?? row.name_as_appears} />
          {row.party_id && (
            <AddPhoneInline partyId={row.party_id} hasPhone={!!row.has_phone} onSaved={onMutated} />
          )}
        </div>
      </div>
      {isExpanded && <DocsPanel state={docsState} />}
      </div>
    );
  }
  if (row.bucket === 'suggested') {
    return (
      <div className="border-b border-border last:border-b-0">
      <div className="grid grid-cols-[2fr_1.5fr_1.2fr_120px] items-center gap-4 px-4 py-3">
        <div>
          <div className="text-[13px] text-text">
            {onToggleExpand && (
              <button
                onClick={onToggleExpand}
                title={isExpanded ? 'Hide documents' : 'Show this suggested party\'s documents in this docket\'s TRS'}
                className="inline-flex items-center justify-center w-4 text-text-faint hover:text-accent text-[11px] mr-0.5 align-middle"
              >{isExpanded ? '▾' : '▸'}</button>
            )}
            {row.name_as_appears}
            <PushedIndicator pushedAt={row.last_pushed_to_close_at} className="ml-1.5" />
            <LeadListIndicator status={row.lead_list_status}         className="ml-1.5" />
            <SkipMarker      skippedAt={row.skipped_at}              className="ml-1.5" />
            {row.has_sold_minerals && (
              <span
                title="Conveyed minerals or royalty within this docket's TRS"
                className="ml-1.5 px-1.5 py-0.5 text-[9px] font-mono uppercase tracking-wider rounded bg-text-faint/15 text-text-faint align-middle"
              >sold</span>
            )}
            <HasContactIndicator hasPhone={!!row.has_phone} hasAddress={!!row.has_address} className="ml-1.5 align-middle" />
          </div>
          <div className="text-[10px] text-text-faint font-mono mt-0.5">
            could be → {row.suggested_party_id ? (
              <Link to={`/mi/party/${row.suggested_party_id}`} className="text-accent/80 hover:underline">{row.suggested_display_name}</Link>
            ) : <span className="text-accent/80">{row.suggested_display_name}</span>}
          </div>
          <PartyContextChips row={row} />
        </div>
        <div className="flex items-center gap-2">
          <span className={'px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider rounded ' + entityChipClass(row.suggested_entity_type)}>
            {row.suggested_entity_type ?? '—'}
          </span>
          <span className="text-[10px] font-mono text-amber">
            sim {row.suggested_match_confidence?.toFixed(2)}
          </span>
        </div>
        <div>
          {row.parse_warnings.length > 0 && (
            <div className="text-[10px] text-amber font-mono">{row.parse_warnings[0]}</div>
          )}
          {rowErr && <div className="text-[10px] text-red font-mono mt-0.5">{rowErr}</div>}
        </div>
        <div className="flex items-center justify-end gap-1.5">
          <button onClick={() => withBusy('confirm', () => poolOrderApi.confirmSuggestion(row.pool_order_name_id))}
            disabled={busy !== null}
            className="px-2 py-1 text-[10px] border border-accent text-accent rounded hover:bg-accent-soft disabled:opacity-50">
            {busy === 'confirm' ? '…' : 'Confirm'}
          </button>
          <button onClick={() => withBusy('reject', () => poolOrderApi.rejectSuggestion(row.pool_order_name_id))}
            disabled={busy !== null}
            className="px-2 py-1 text-[10px] border border-border text-text-faint rounded hover:text-text disabled:opacity-50">
            {busy === 'reject' ? '…' : 'Reject'}
          </button>
        </div>
      </div>
      {isExpanded && <DocsPanel state={docsState} />}
      </div>
    );
  }
  // watch_list
  return (
    <div className="grid grid-cols-[60px_2fr_3fr_120px] items-start gap-4 px-4 py-3 border-b border-border last:border-b-0 hover:bg-bg-hover/30">
      <div><ScoreBadge score={row.investment_score} /></div>
      <div>
        <div className="text-[13px] text-text">
          {row.name_as_appears}
          <PushedIndicator pushedAt={row.last_pushed_to_close_at} className="ml-1.5" />
          <LeadListIndicator status={row.lead_list_status}         className="ml-1.5" />
          <SkipMarker      skippedAt={row.skipped_at}              className="ml-1.5" />
          <HasContactIndicator hasPhone={!!row.has_phone} hasAddress={!!row.has_address} className="ml-1.5 align-middle" />
        </div>
        {row.parse_warnings.length > 0 && (
          <div className="text-[10px] text-amber font-mono mt-0.5">⚠ {row.parse_warnings[0]}</div>
        )}
      </div>
      <div className="text-[11px] text-text-dim leading-snug">
        {row.investment_reasoning ?? <span className="text-text-ghost italic">not scored yet</span>}
      </div>
      <div className="flex items-center justify-end gap-1.5">
        {rowErr && <span className="text-[10px] text-red font-mono">{rowErr}</span>}
        <CopyButton text={stripPoolOrderAndSuffix(row.name_as_appears)}
          title={`Copy "${stripPoolOrderAndSuffix(row.name_as_appears)}" for TruePeopleSearch`} />
        <button onClick={() => withBusy('promote', () => poolOrderApi.promote(row.pool_order_name_id))}
          disabled={busy !== null}
          title="Create a party row for this name so it can carry a phone, address, and Close link"
          className="px-2 py-1 text-[10px] border border-accent text-accent rounded hover:bg-accent-soft disabled:opacity-50">
          {busy === 'promote' ? '…' : 'Promote'}
        </button>
      </div>
    </div>
  );
}

// Auto-recovery banners. When parse_status='complete' but the optional
// Vision calls (Names list and/or Appendix A) timed out during ingest,
// auto-fire the dedicated re-extract endpoints (which have full retry
// budget + no client-side timeout) in the background. The operator
// never has to click. After 2 auto-attempts that both fail, surface a
// manual "Try once more" button — but only after auto exhausted, so
// the common case stays click-free.
//
// State machine per item (names, appendix_a):
//   idle (entry)
//     ↓ effect fires when needs* && attempts < 2 && status === 'idle'
//   recovering (request in flight)
//     ↓ success → 'success' (banner disappears once data reloads)
//     ↓ failure & attempts < 2 → 'idle' (effect re-fires)
//     ↓ failure & attempts >= 2 → 'failed_after_retries' (manual button)
//
// Manual button click → reset to attempts=0, status='idle', triggers
// the effect to fire one more auto-attempt.
type RecoveryStatus = 'idle' | 'recovering' | 'success' | 'failed_after_retries';
interface RecoveryState {
  status: RecoveryStatus;
  attempts: number;
  error: string | null;
}
const RECOVERY_INIT: RecoveryState = { status: 'idle', attempts: 0, error: null };
const AUTO_MAX_ATTEMPTS = 2;

function AutoRecoveryBanners({
  data,
  onReload,
}: {
  data: PoolOrderDetailResponse;
  onReload: () => void | Promise<void>;
}) {
  const id = data.pool_order.id;
  const po = data.pool_order;
  const [namesState, setNamesState] = useState<RecoveryState>(RECOVERY_INIT);
  const [appendixState, setAppendixState] = useState<RecoveryState>(RECOVERY_INIT);

  // Need-recovery detection. Gate on the positive completion stamps
  // (migration 017): NULL means the upload-time Vision call never
  // returned a parseable response, so we should auto-retry. Non-NULL
  // means Vision completed (possibly with 0 rows — legitimate empty
  // case for name-only dockets) and we should leave it alone.
  // Recovery is only meaningful when the PDF is on R2 (otherwise the
  // dedicated endpoint will 400 / 404 immediately).
  // Names extraction can run against either a dedicated names PDF or
  // (as a fallback) the application PDF, since CO pooling apps often
  // bundle the Interested Parties exhibit inside the application body.
  // So we surface the recovery banner whenever EITHER source is on R2
  // and names_extracted_at is still NULL. extract-names handles the
  // fallback server-side. Appendix A only ever lives in the application
  // PDF, so its gate stays narrower.
  const needsNames    = po.names_extracted_at      === null
                        && (po.pdf_names_r2_key       !== null
                         || po.pdf_application_r2_key !== null);
  const needsAppendix = po.appendix_a_extracted_at === null
                        && po.pdf_application_r2_key !== null;

  async function fireRecovery(kind: 'names' | 'appendix', currentAttempts: number) {
    const setter = kind === 'names' ? setNamesState : setAppendixState;
    setter({ status: 'recovering', attempts: currentAttempts + 1, error: null });
    try {
      if (kind === 'names') await poolOrderApi.extractNames(id);
      else                  await poolOrderApi.extractAppendixA(id);
      await onReload();
      // After reload, needs* should flip false (rows now exist) and the
      // banner unmounts. Mark success anyway in case the recovery
      // returned 0 rows (extraction succeeded but PDF had nothing).
      setter(s => ({ status: 'success', attempts: s.attempts, error: null }));
    } catch (e: any) {
      const nextAttempts = currentAttempts + 1;
      const errMsg = e?.message ?? 'extraction failed';
      if (nextAttempts < AUTO_MAX_ATTEMPTS) {
        // Re-arm: status back to idle triggers the effect to fire again.
        setter({ status: 'idle', attempts: nextAttempts, error: errMsg });
      } else {
        setter({ status: 'failed_after_retries', attempts: nextAttempts, error: errMsg });
      }
    }
  }

  // Auto-fire effects, one per item. Gate on status === 'idle' AND
  // attempts < AUTO_MAX_ATTEMPTS so each attempt counts exactly once.
  useEffect(() => {
    if (needsNames && namesState.status === 'idle' && namesState.attempts < AUTO_MAX_ATTEMPTS) {
      fireRecovery('names', namesState.attempts);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [needsNames, namesState.status, namesState.attempts, id]);

  useEffect(() => {
    if (needsAppendix && appendixState.status === 'idle' && appendixState.attempts < AUTO_MAX_ATTEMPTS) {
      fireRecovery('appendix', appendixState.attempts);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [needsAppendix, appendixState.status, appendixState.attempts, id]);

  function manualRetry(kind: 'names' | 'appendix') {
    // Reset attempts so the auto-effect picks it up immediately.
    const setter = kind === 'names' ? setNamesState : setAppendixState;
    setter(RECOVERY_INIT);
  }

  // Render: one banner per item still in a recovery state. Hide
  // success banners (the populated tabs are the success indicator).
  const banners: React.ReactNode[] = [];

  if (needsNames && namesState.status !== 'success') {
    banners.push(
      <RecoveryBanner
        key="names"
        label="Names list"
        state={namesState}
        onManualRetry={() => manualRetry('names')}
      />
    );
  }
  if (needsAppendix && appendixState.status !== 'success') {
    banners.push(
      <RecoveryBanner
        key="appendix"
        label="Appendix A leased owners"
        state={appendixState}
        onManualRetry={() => manualRetry('appendix')}
      />
    );
  }

  if (banners.length === 0) return null;
  return <div className="mt-4 space-y-2">{banners}</div>;
}

function RecoveryBanner({
  label,
  state,
  onManualRetry,
}: {
  label: string;
  state: RecoveryState;
  onManualRetry: () => void;
}) {
  if (state.status === 'recovering') {
    return (
      <div className="border border-accent/30 bg-accent-soft rounded px-4 py-2.5 flex items-center gap-3">
        <span className="inline-block w-3 h-3 border-2 border-accent border-r-transparent rounded-full animate-spin" />
        <div className="text-[12px] text-text">
          <strong>{label}</strong> extraction failed during upload. Auto-retrying… <span className="text-text-faint font-mono ml-1">(attempt {state.attempts}/{AUTO_MAX_ATTEMPTS})</span>
        </div>
      </div>
    );
  }
  if (state.status === 'failed_after_retries') {
    return (
      <div className="border border-amber/40 bg-amber/10 rounded px-4 py-2.5 flex items-center justify-between gap-3">
        <div className="text-[12px] text-text">
          <strong>{label}</strong> extraction failed {state.attempts} times.
          {state.error && <span className="text-text-faint font-mono text-[10px] ml-2">{state.error}</span>}
        </div>
        <button onClick={onManualRetry}
          className="shrink-0 px-3 py-1.5 text-[11px] bg-amber/20 text-amber border border-amber/40 rounded hover:bg-amber/30 font-medium">
          Try once more
        </button>
      </div>
    );
  }
  // 'idle' between auto-attempts: show the same spinner shape so the UI
  // doesn't flicker dark while we re-arm. attempts increments shows the
  // operator something is still happening.
  return (
    <div className="border border-accent/20 bg-accent-soft/60 rounded px-4 py-2.5 flex items-center gap-3">
      <span className="inline-block w-3 h-3 border-2 border-accent/50 border-r-transparent rounded-full animate-spin" />
      <div className="text-[12px] text-text-dim">
        <strong>{label}</strong> extraction queued… <span className="text-text-faint font-mono ml-1">(attempt {state.attempts + 1}/{AUTO_MAX_ATTEMPTS})</span>
      </div>
    </div>
  );
}

// Per-party context chips rendered under name lines on matched +
// suggested rows. Two operationally-meaningful flags:
//   "Aggregator"  party appears as grantee on a MINERAL DEED in the
//                 last 24 months — de-prioritize visually.
//   "Old lease"   party's most recent grantor doc is a LEASE or
//                 LEASE RELEASE in the last 5 years — Pattern A:
//                 long-time owner with prior leasing relationship.
// Sub-row docs table for the matched/suggested expand chevron.
// Mirrors AoiDetail's PartyDocsExpansion. Lazy load happens at the
// page level so the cache survives sort/filter changes without
// re-fetching on every collapse → re-expand.
function DocsPanel({ state }: { state: RosterRowDoc[] | 'loading' | 'error' | undefined }) {
  if (state === undefined || state === 'loading') {
    return <div className="px-4 pb-3 pl-12 text-[11px] font-mono text-text-faint">loading documents…</div>;
  }
  if (state === 'error') {
    return <div className="px-4 pb-3 pl-12 text-[11px] font-mono text-red">failed to load documents</div>;
  }
  if (state.length === 0) {
    return <div className="px-4 pb-3 pl-12 text-[11px] font-mono text-text-faint italic">no documents in this docket's TRS</div>;
  }
  return (
    <div className="px-4 pb-3 pl-12">
      <div className="bg-bg-card-soft border border-border rounded overflow-hidden">
        <div className="grid grid-cols-[100px_180px_2fr_120px_36px] gap-3 px-3 py-1.5 bg-bg-input/40 border-b border-border text-[10px] uppercase tracking-wider font-mono text-text-faint">
          <div>Recorded</div>
          <div>Type</div>
          <div>TRS</div>
          <div className="text-right">Doc #</div>
          <div></div>
        </div>
        {state.map(d => (
          <div key={`${d.doc_number}-${d.ordinal}`}
            className="grid grid-cols-[100px_180px_2fr_120px_36px] gap-3 items-center px-3 py-1.5 border-b border-border last:border-b-0 text-[11px] font-mono hover:bg-bg-hover/30">
            <span className="text-text-dim">{d.recording_date ?? '—'}</span>
            <span className="text-text truncate" title={d.doc_type ?? undefined}>{d.doc_type ?? '—'}</span>
            <span className="text-text-dim truncate" title={d.trs_label ?? undefined}>{d.trs_label ?? '—'}</span>
            <Link to={`/mi/document/${d.doc_number}`} className="text-accent hover:underline text-right" tabIndex={-1}>
              {d.doc_number}
            </Link>
            <div className="flex justify-end">
              <CopyButton text={d.doc_number} title={`Copy ${d.doc_number} to clipboard`} tabIndex={-1} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function PartyContextChips({ row }: { row: RosterEntry }) {
  if (!row.has_recent_acquisition && !row.most_recent_lease_kind) return null;
  return (
    <div className="mt-1 flex items-center gap-1.5 flex-wrap">
      {row.most_recent_lease_kind && (
        <span className="px-1.5 py-0.5 text-[9px] font-mono uppercase tracking-wider rounded border border-blue/40 bg-blue/10 text-blue"
          title={`Most recent grantor doc was a ${row.most_recent_lease_kind}${row.most_recent_lease_date ? ' on ' + row.most_recent_lease_date : ''}`}>
          Old lease{row.most_recent_lease_date ? ` · ${row.most_recent_lease_date.slice(0, 4)}` : ''}
        </span>
      )}
      {row.has_recent_acquisition && (
        <span className="px-1.5 py-0.5 text-[9px] font-mono uppercase tracking-wider rounded border border-amber/40 bg-amber/10 text-amber"
          title="Party appears as grantee on a MINERAL DEED in the last 24 months — likely an aggregator/buyer">
          Aggregator
        </span>
      )}
      {row.oldest_activity && (
        <span className="px-1.5 py-0.5 text-[9px] font-mono text-text-faint"
          title="Oldest grantor recording_date in our corpus">
          since {row.oldest_activity.slice(0, 4)}
        </span>
      )}
    </div>
  );
}

// Leased-owners (Appendix A) panel. Read-only at this stage; rows are
// pre-cross-referenced at ingest time so all link rendering is local.
function LeasedOwnersPanel({
  rows,
  counts,
  operatorAvgTermMonths,
}: {
  rows: LeasedOwnerEntry[];
  counts: PoolOrderDetailResponse['counts'];
  operatorAvgTermMonths: number | null;
}) {
  const [sort, setSort] = useState<'newest' | 'oldest'>('newest');
  const [expiringOnly, setExpiringOnly] = useState(false);

  // Filter is shown only when we have a real per-operator term avg. Q3
  // answer: don't fake-infer when primary_term is missing.
  const canFilterExpiring = operatorAvgTermMonths !== null && operatorAvgTermMonths > 0;
  const todayMs = Date.now();
  const horizonMs = 365 * 24 * 60 * 60 * 1000; // 12 months ahead

  const isApproachingExpiration = (lease_date: string | null): boolean => {
    if (!canFilterExpiring || !lease_date) return false;
    const t = Date.parse(lease_date);
    if (Number.isNaN(t)) return false;
    const expiresMs = t + operatorAvgTermMonths! * 30 * 24 * 60 * 60 * 1000;
    return expiresMs >= todayMs && expiresMs - todayMs <= horizonMs;
  };

  const sorted = useMemo(() => {
    const base = expiringOnly ? rows.filter(r => isApproachingExpiration(r.lease_date)) : rows;
    return [...base].sort((a, b) => {
      const at = a.lease_date ? Date.parse(a.lease_date) : 0;
      const bt = b.lease_date ? Date.parse(b.lease_date) : 0;
      return sort === 'newest' ? bt - at : at - bt;
    });
  }, [rows, sort, expiringOnly, operatorAvgTermMonths]);

  if (rows.length === 0) {
    return (
      <div className="mt-3 px-4 py-8 text-center text-[12px] text-text-faint font-mono">
        no Appendix-A leased owners on this docket
      </div>
    );
  }

  const unknownRows = rows.filter(r => r.party_id === null);

  return (
    <div className="mt-3">
      {unknownRows.length > 0 && (
        <UnknownLeasedOwnersCallout rows={unknownRows} />
      )}

      <div className="flex items-center justify-between mb-3 px-1">
        <div className="text-[11px] text-text-dim font-mono">
          {counts.leased_owners} rows
          <span className="text-text-faint mx-2">·</span>
          {counts.leased_owners_doc_matched} matched to recorder
          <span className="text-text-faint mx-2">·</span>
          {counts.leased_owners_lessor_matched} lessors linked
          <span className="text-text-faint mx-2">·</span>
          {counts.leased_owners_lessee_matched} lessees linked
          {(() => {
            const withCo = rows.filter(r => r.co_lessor_parties && r.co_lessor_parties.length > 0).length;
            const coLinks = rows.reduce((n, r) => n + (r.co_lessor_parties?.length ?? 0), 0);
            return withCo > 0 ? (
              <>
                <span className="text-text-faint mx-2">·</span>
                <span className="text-blue" title={`${coLinks} additional party links across ${withCo} joint-tenancy rows`}>
                  {withCo} co-lessor row{withCo === 1 ? '' : 's'}
                </span>
              </>
            ) : null;
          })()}
          {counts.leased_owners_unknown > 0 && (
            <>
              <span className="text-text-faint mx-2">·</span>
              <span className="text-amber">{counts.leased_owners_unknown} unknown</span>
            </>
          )}
        </div>
        <div className="flex items-center gap-3">
          {canFilterExpiring && (
            <label className="text-[11px] text-text-dim font-mono flex items-center gap-1.5 cursor-pointer">
              <input type="checkbox" checked={expiringOnly} onChange={e => setExpiringOnly(e.target.checked)}
                className="accent-accent" />
              expiring &lt; 12mo
              <span className="text-text-faint">(avg term {operatorAvgTermMonths}mo)</span>
            </label>
          )}
          <select value={sort} onChange={e => setSort(e.target.value as 'newest' | 'oldest')}
            className="text-[11px] bg-bg-input border border-border rounded px-2 py-1 font-mono text-text-dim">
            <option value="newest">Newest lease</option>
            <option value="oldest">Oldest lease</option>
          </select>
        </div>
      </div>

      <div className="grid grid-cols-[110px_2fr_1.5fr_90px_120px_60px] gap-3 px-3 py-2 text-[10px] uppercase tracking-wider font-mono text-text-faint border-b border-border">
        <div>Lease date</div>
        <div>Lessor</div>
        <div>Lessee</div>
        <div>TRS</div>
        <div>Reception</div>
        <div className="text-right">Warn</div>
      </div>

      {sorted.map(o => <LeasedOwnerRow key={o.id} row={o} />)}

      {sorted.length === 0 && (
        <div className="px-4 py-8 text-center text-[12px] text-text-faint font-mono">
          no rows match this filter
        </div>
      )}
    </div>
  );
}

function LeasedOwnerRow({ row }: { row: LeasedOwnerEntry }) {
  const trs = row.township_num != null && row.range_num != null && row.section != null
    ? `T${row.township_num}${row.township_dir ?? ''} R${row.range_num}${row.range_dir ?? ''} ${row.section}`
    : '—';

  return (
    <div className="grid grid-cols-[110px_2fr_1.5fr_90px_120px_60px] gap-3 items-start px-3 py-3 border-b border-border last:border-b-0 hover:bg-bg-hover/30">
      <div className="text-[11px] font-mono text-text-dim">
        {row.lease_date ?? <span className="text-text-ghost italic">—</span>}
      </div>
      <div>
        {row.party_id ? (
          <Link to={`/mi/party/${row.party_id}`} className="text-[13px] text-text hover:text-accent">
            {row.lessor_party_display_name_natural ?? row.lessor_party_display_name ?? row.lessor_name}
          </Link>
        ) : (
          <div className="text-[13px] text-text">{row.lessor_name}</div>
        )}
        <div className="text-[10px] font-mono text-text-faint mt-0.5">
          {row.party_id
            ? <span className="text-green">● linked {row.match_confidence?.toFixed(2)}</span>
            : row.suggested_party_id
              ? <span className="text-amber">~ suggested {row.suggested_lessor_display_name_natural ?? row.suggested_lessor_display_name} ({row.suggested_confidence?.toFixed(2)})</span>
              : <span className="text-text-ghost">unmatched</span>}
        </div>
        {row.co_lessor_parties && row.co_lessor_parties.length > 0 && (
          <details className="mt-1">
            <summary className="text-[10px] font-mono text-blue cursor-pointer hover:text-accent">
              + {row.co_lessor_parties.length} co-lessor{row.co_lessor_parties.length === 1 ? '' : 's'}
            </summary>
            <div className="mt-1 pl-3 border-l border-border space-y-0.5">
              {row.co_lessor_parties.map(co => (
                <div key={co.party_id} className="text-[10px] font-mono">
                  <Link to={`/mi/party/${co.party_id}`} className="text-text-dim hover:text-accent">
                    {co.display_name_natural}
                  </Link>
                  <span className="text-text-faint ml-1">{co.match_confidence.toFixed(2)}</span>
                </div>
              ))}
            </div>
          </details>
        )}
        {row.tract_description && (
          <div className="text-[10px] text-text-faint mt-0.5 truncate" title={row.tract_description}>
            {row.tract_description}
          </div>
        )}
      </div>
      <div>
        {row.lessee_party_id ? (
          <Link to={`/mi/party/${row.lessee_party_id}`} className="text-[12px] text-text-dim hover:text-accent">
            {row.lessee_party_display_name_natural ?? row.lessee_party_display_name ?? row.lessee_name}
          </Link>
        ) : (
          <div className="text-[12px] text-text-dim">{row.lessee_name ?? <span className="text-text-ghost italic">—</span>}</div>
        )}
        {row.lessee_party_id && (
          <div className="text-[10px] font-mono text-green mt-0.5">● linked {row.lessee_confidence?.toFixed(2)}</div>
        )}
      </div>
      <div className="text-[11px] font-mono text-text-dim">{trs}</div>
      <div className="text-[11px] font-mono">
        {row.matched_doc_number ? (
          <Link to={`/mi/document/${row.matched_doc_number}`}
            className="text-accent hover:underline" title={row.matched_doc_recording_date ?? ''}>
            {row.reception_number ?? row.matched_doc_number}
          </Link>
        ) : row.reception_number ? (
          <span className="text-text-faint" title="reception number on Appendix A row did not match a doc in our corpus">
            {row.reception_number}
          </span>
        ) : (
          <span className="text-text-ghost">—</span>
        )}
      </div>
      <div className="text-right">
        {row.parse_warnings.length > 0 && (
          <span className="text-amber text-[12px]" title={row.parse_warnings.join('; ')}>⚠</span>
        )}
      </div>
    </div>
  );
}

// Appendix-A rows where party_id is NULL — Vision extracted them but
// the lessor name didn't trigram-match any party in our corpus. These
// are unknowns: either someone we've never seen file in the area (likely
// a recent acquirer whose deed hasn't hit our ingest yet), or a name
// variant the matcher missed. Either way they deserve manual eyes
// before any docket is acted on, so they sit at the top of Leased
// owners as a prominent amber callout, not buried in the table.
function UnknownLeasedOwnersCallout({ rows }: { rows: LeasedOwnerEntry[] }) {
  return (
    <div className="mb-4 border border-amber/40 bg-amber/10 rounded">
      <div className="px-4 py-2.5 border-b border-amber/30 flex items-center gap-2">
        <span className="text-amber text-[14px]">⚠</span>
        <div className="text-[12px] text-text">
          <strong>{rows.length}</strong> {rows.length === 1 ? 'leased owner is' : 'leased owners are'} on Appendix A but unknown to our corpus.
        </div>
        <div className="text-[10px] text-text-faint font-mono ml-auto">
          may be recent acquirers, name variants the matcher missed, or operator typos
        </div>
      </div>
      <div className="divide-y divide-amber/20">
        {rows.map(r => {
          const trs = r.township_num != null && r.range_num != null && r.section != null
            ? `T${r.township_num}${r.township_dir ?? ''} R${r.range_num}${r.range_dir ?? ''} ${r.section}`
            : null;
          return (
            <div key={r.id} className="grid grid-cols-[100px_2fr_1.5fr_90px_120px] gap-3 items-start px-4 py-2">
              <div className="text-[11px] font-mono text-text-dim">{r.lease_date ?? '—'}</div>
              <div>
                <div className="text-[12px] text-text">{r.lessor_name}</div>
                {r.tract_description && (
                  <div className="text-[10px] text-text-faint mt-0.5 truncate" title={r.tract_description}>
                    {r.tract_description}
                  </div>
                )}
              </div>
              <div className="text-[11px] text-text-dim truncate" title={r.lessee_name ?? ''}>
                lessee: {r.lessee_name ?? <span className="text-text-ghost italic">—</span>}
              </div>
              <div className="text-[11px] font-mono text-text-dim">{trs ?? '—'}</div>
              <div className="text-[11px] font-mono text-right">
                {r.matched_doc_number ? (
                  <Link to={`/mi/document/${r.matched_doc_number}`} className="text-accent hover:underline">
                    {r.reception_number ?? r.matched_doc_number}
                  </Link>
                ) : r.reception_number ? (
                  <span className="text-text-faint" title="reception did not match any doc in our corpus">{r.reception_number}</span>
                ) : (
                  <span className="text-text-ghost">no reception</span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Right-sidebar card holding the Opus 4.7 analysis of this docket.
// Empty state shows a Generate button. After the first analysis lands
// the card renders structured sections with clickable party links
// (per pick) and a Regenerate button.
// PoolOrderMapImageCard — paste a map screenshot into the docket
// right-rail. Direct port of AoiDetail's MapImageCard against the
// pool-order endpoint. tabIndex=0 + onPaste so click-then-paste
// works; drag-drop also accepted. Cache-buster keyed off
// uploadedAt + a local mutation counter so re-paste re-fetches the
// bytes.
function PoolOrderMapImageCard(props: {
  poolOrderId: string;
  hasImage: boolean;
  uploadedAt: string | null;
  onChange: () => Promise<void> | void;
}) {
  const { poolOrderId, hasImage, uploadedAt, onChange } = props;
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [bust, setBust] = useState(0);

  async function uploadBlob(blob: Blob) {
    setUploading(true);
    setErr(null);
    try {
      await poolOrderApi.uploadMapImage(poolOrderId, blob);
      setBust(b => b + 1);
      await onChange();
    } catch (e: any) {
      setErr(e?.message ?? 'Upload failed');
    } finally {
      setUploading(false);
    }
  }

  function onPaste(e: React.ClipboardEvent<HTMLDivElement>) {
    const items = e.clipboardData?.items;
    if (!items) return;
    for (const it of Array.from(items)) {
      if (it.kind === 'file' && it.type.startsWith('image/')) {
        e.preventDefault();
        const blob = it.getAsFile();
        if (blob) uploadBlob(blob);
        return;
      }
    }
  }

  function onDrop(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault();
    const file = e.dataTransfer?.files?.[0];
    if (file && file.type.startsWith('image/')) uploadBlob(file);
  }

  async function clearImage() {
    if (!confirm('Remove map image?')) return;
    setUploading(true);
    setErr(null);
    try {
      await poolOrderApi.deleteMapImage(poolOrderId);
      setBust(b => b + 1);
      await onChange();
    } catch (e: any) {
      setErr(e?.message ?? 'Delete failed');
    } finally {
      setUploading(false);
    }
  }

  const imgSrc = hasImage
    ? `/api/mi/pool-orders/${poolOrderId}/map-image?v=${encodeURIComponent(uploadedAt ?? '')}-${bust}`
    : null;

  return (
    <div
      tabIndex={0}
      onPaste={onPaste}
      onDragOver={e => e.preventDefault()}
      onDrop={onDrop}
      className="bg-bg-card border border-border rounded p-3 focus:outline-none focus:border-accent/60"
      title="Click here, then paste (⌘V / Ctrl-V) a map screenshot"
    >
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] uppercase tracking-wider font-mono text-text-faint">Map</div>
        {hasImage && (
          <button
            onClick={clearImage}
            disabled={uploading}
            className="text-[10px] font-mono text-text-faint hover:text-red"
          >× remove</button>
        )}
      </div>
      {imgSrc ? (
        <>
          <img
            src={imgSrc}
            alt="Pool order map"
            className="block w-full rounded border border-border"
            onError={() => setErr('Image failed to load')}
          />
          {uploadedAt && (
            <div className="text-[10px] font-mono text-text-faint mt-1.5">
              pasted {uploadedAt.slice(0, 10)}
            </div>
          )}
        </>
      ) : (
        <div className="border border-dashed border-border rounded px-3 py-6 text-center">
          <div className="text-[12px] text-text-dim">Click here, then paste a map screenshot</div>
          <div className="text-[10px] font-mono text-text-faint mt-1">⌘V / Ctrl-V · or drop an image</div>
        </div>
      )}
      {uploading && (
        <div className="text-[10px] font-mono text-accent mt-2">uploading…</div>
      )}
      {err && (
        <div className="text-[11px] text-red bg-red/10 border border-red/40 rounded px-2 py-1 mt-2">{err}</div>
      )}
    </div>
  );
}

function ClaudeAnalysisCard({
  poolOrderId,
  cached,
  onAnalyzed,
}: {
  poolOrderId: string;
  cached: { analysis: PoolOrderAnalysis; meta: PoolOrderAnalysisMeta } | null;
  onAnalyzed: () => void | Promise<void>;
}) {
  const [running, setRunning] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function run() {
    setErr(null);
    setRunning(true);
    try {
      await poolOrderApi.analyze(poolOrderId);
      await onAnalyzed();
    } catch (e: any) {
      setErr(e?.message ?? 'analyze failed');
    } finally {
      setRunning(false);
    }
  }

  // Empty state — placeholder is gone; this is the new resting state.
  if (!cached) {
    return (
      <div className="bg-bg-card border border-border rounded p-4">
        <div className="text-[10px] uppercase tracking-wider font-mono text-text-faint mb-2">Claude · pool order read</div>
        <div className="text-[12px] text-text-dim mb-3">
          No analysis yet. Generate a structured read with Opus 4.7 — usually ~$0.20 and 5-10s.
        </div>
        <button onClick={run} disabled={running}
          className="w-full px-3 py-2 text-[12px] bg-accent text-bg font-medium rounded hover:bg-accent-bright disabled:opacity-50">
          {running ? 'Analyzing (~10s)…' : 'Generate analysis →'}
        </button>
        {err && <div className="mt-2 text-[10px] text-red font-mono">{err}</div>}
      </div>
    );
  }

  const { analysis, meta } = cached;
  return (
    <div className="bg-bg-card border border-border rounded p-4 space-y-4">
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-wider font-mono text-text-faint">Claude · pool order read</div>
        <button onClick={run} disabled={running}
          className="text-[10px] font-mono text-text-faint hover:text-accent disabled:opacity-50"
          title="Re-run with current data — replaces the cached analysis">
          {running ? '↻ regenerating…' : '↻ regenerate'}
        </button>
      </div>

      {err && <div className="text-[10px] text-red font-mono">{err}</div>}

      <div className="text-[12px] text-text leading-relaxed">{analysis.thesis_paragraph}</div>

      {analysis.urgent_outreach_top_3.length > 0 && (
        <AnalysisPickList label="Urgent outreach" tone="accent" picks={analysis.urgent_outreach_top_3} />
      )}

      {analysis.lower_priority_top_3.length > 0 && (
        <AnalysisPickList label="Lower priority" tone="dim" picks={analysis.lower_priority_top_3} />
      )}

      {analysis.risk_flags.length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-wider font-mono text-text-faint mb-1.5">Risks</div>
          <div className="space-y-1.5">
            {analysis.risk_flags.map((r, i) => <RiskFlagRow key={i} flag={r} />)}
          </div>
        </div>
      )}

      {analysis.comparable_recent_dockets.length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-wider font-mono text-text-faint mb-1.5">Comparable prior dockets</div>
          <div className="space-y-1.5">
            {analysis.comparable_recent_dockets.map(c => (
              <div key={c.docket_number} className="text-[11px] leading-snug">
                <span className="text-accent font-mono">{c.docket_number}</span>
                {c.filed_date && <span className="text-text-faint font-mono ml-1">· {c.filed_date}</span>}
                <div className="text-text-dim">{c.why_comparable}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {analysis.operator_pattern_note && (
        <div>
          <div className="text-[10px] uppercase tracking-wider font-mono text-text-faint mb-1.5">Operator pattern</div>
          <div className="text-[11px] text-text-dim leading-snug">{analysis.operator_pattern_note}</div>
        </div>
      )}

      {analysis.notes && (
        <div className="text-[11px] text-text-dim leading-snug italic border-l-2 border-border pl-3">
          {analysis.notes}
        </div>
      )}

      <div className="pt-2 border-t border-border text-[10px] font-mono text-text-faint flex justify-between">
        <span>{meta.model ?? '—'} · {meta.prompt_version ?? '—'}</span>
        <span>
          {meta.generated_at?.slice(0, 16).replace('T', ' ')}
          {meta.cost_usd != null && <> · ${meta.cost_usd.toFixed(2)}</>}
        </span>
      </div>
    </div>
  );
}

function AnalysisPickList({ label, tone, picks }: { label: string; tone: 'accent' | 'dim'; picks: AnalysisPick[] }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider font-mono text-text-faint mb-1.5">{label}</div>
      <div className="space-y-2">
        {picks.map(p => (
          <div key={p.party_id} className="text-[11px] leading-snug">
            <div className="flex items-baseline justify-between gap-2">
              <Link to={`/mi/party/${p.party_id}`}
                className={tone === 'accent' ? 'text-accent hover:underline font-medium' : 'text-text-dim hover:text-accent hover:underline'}>
                {p.display_name_natural}
              </Link>
              <span className="text-[10px] font-mono text-text-faint shrink-0">{p.score}</span>
            </div>
            <div className="text-text-dim">{p.reason}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

const RISK_TONE: Record<AnalysisRiskFlag['kind'], string> = {
  operator_new_entrant:  'border-accent/40 bg-accent-soft text-accent-bright',
  appendix_a_incomplete: 'border-amber/40 bg-amber/10 text-amber',
  formation_unusual:     'border-blue/40 bg-blue/10 text-blue',
  hearing_soon:          'border-red/50 bg-red/10 text-red',
  data_quality:          'border-amber/40 bg-amber/10 text-amber',
  other:                 'border-border bg-bg-input/40 text-text-dim',
};

function RiskFlagRow({ flag }: { flag: AnalysisRiskFlag }) {
  return (
    <div className="text-[11px] leading-snug flex gap-2">
      <span className={'shrink-0 px-1.5 py-0.5 text-[9px] font-mono uppercase tracking-wider rounded border ' + (RISK_TONE[flag.kind] ?? RISK_TONE.other)}>
        {flag.kind.replace(/_/g, ' ')}
      </span>
      <span className="text-text-dim">{flag.detail}</span>
    </div>
  );
}
